import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const AdBanner = () => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imgIndex, setImgIndex] = useState(0);

    useEffect(() => {
        fetchAds();
    }, []);

    const imageAds = ads.filter(ad => ad.adType === 'image' && ad.imageUrl);
    const textAds = ads.filter(ad => !(ad.adType === 'image' && ad.imageUrl));

    // Auto-rotate image ad slides
    useEffect(() => {
        if (imageAds.length <= 1) return;
        const interval = setInterval(() => {
            setImgIndex((prev) => (prev + 1) % imageAds.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [imageAds.length]);

    useEffect(() => {
        if (imgIndex >= imageAds.length) setImgIndex(0);
    }, [imageAds.length, imgIndex]);

    const fetchAds = async () => {
        try {
            const response = await api.get('/ads?position=top&isActive=true');
            setAds(response.data.ads || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching ads:', error);
            setLoading(false);
        }
    };

    const handleClick = async (ad) => {
        if (ad) {
            try {
                await api.post(`/ads/${ad._id}/click`);
                window.open(ad.redirectUrl, '_blank');
            } catch (error) {
                console.error('Error tracking click:', error);
                window.open(ad.redirectUrl, '_blank');
            }
        }
    };

    if (loading || ads.length === 0) return null;

    // Duplicate text ads for seamless scrolling
    const scrollingAds = [...textAds, ...textAds];

    // Beautiful gradient backgrounds (rotating)
    const gradients = [
        'from-pink-600 via-red-500 to-orange-500',
        'from-purple-600 via-indigo-500 to-blue-500',
        'from-green-600 via-teal-500 to-cyan-500',
        'from-yellow-600 via-amber-500 to-orange-500',
        'from-rose-600 via-pink-500 to-purple-500',
        'from-emerald-600 via-green-500 to-teal-500',
    ];

    return (
        <div className="w-full overflow-hidden">
            {imageAds.length > 0 && (
                <div className="relative w-full overflow-hidden bg-gray-100">
                    {imageAds.map((ad, index) => (
                        <img
                            key={ad._id}
                            src={ad.imageUrl}
                            alt={ad.title}
                            onClick={() => handleClick(ad)}
                            className={`w-full h-auto max-h-64 object-cover cursor-pointer transition-opacity duration-500 ${index === imgIndex ? 'block' : 'hidden'}`}
                        />
                    ))}
                    {imageAds.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                            {imageAds.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setImgIndex(index)}
                                    aria-label={`Go to ad slide ${index + 1}`}
                                    className={`w-2 h-2 rounded-full transition-colors ${index === imgIndex ? 'bg-white' : 'bg-white/50'}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {textAds.length > 0 && (
            <div className={`relative bg-gradient-to-r ${gradients[0]} py-2.5 md:py-3 overflow-hidden`}>
                {/* Blinking/Shimmer overlay */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                </div>

                <div className="relative flex whitespace-nowrap animate-marquee">
                    {scrollingAds.map((ad, index) => (
                        <div
                            key={index}
                            onClick={() => handleClick(ad)}
                            className="inline-flex items-center gap-3 mx-4 cursor-pointer group"
                        >
                            <span className="text-white text-sm md:text-base font-bold tracking-wide drop-shadow-md">
                                ✨ {ad.message || ad.title} ✨
                            </span>
                            <span className="text-yellow-200 text-xs md:text-sm font-semibold group-hover:text-yellow-100 transition-all whitespace-nowrap underline decoration-1 underline-offset-2">
                                Click for more →
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            )}

            <style jsx>{`
                @keyframes marquee {
                    0% {
                        transform: translateX(0);
                    }
                    100% {
                        transform: translateX(-50%);
                    }
                }
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(100%);
                    }
                }
                .animate-marquee {
                    animation: marquee 25s linear infinite;
                    display: inline-flex;
                }
                .animate-marquee:hover {
                    animation-play-state: paused;
                }
                .animate-shimmer {
                    animation: shimmer 3s infinite;
                }
            `}</style>
        </div>
    );
};

export default AdBanner;